import React, { useEffect, useState } from "react";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsTable from "../ui/LmsTable";
import {
  LmsVotingDetailsTable,
  LmsLoanCommentsTable,
  LmsLoanPropertyTable,
  LmsLoanDetailsEntitiesTable
} from "../ui/LmsSharedTables";
import LmsTabPages from "../ui/LmsTabPages";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { useHistory } from "react-router-dom";
import BCModal from "../../ui/BCModal";
import { AddLoanConditionsForm } from "../Servicing/LmsServicingLoanConditions";

const editLoanReviewFormField = (
  myForm: BCFormController,
  id?,
  setIsAddConditionModalOpen?,
  loanReviewMembersVote: any[] = []
) => {

  myForm.addGroupFields([
    myForm.getDisplayField("Borrower", { name: "borrowerOrganizationDisplay" }),
    myForm.getDisplayField("Application", { name: "applicationNumberDisplay" }),
    myForm.getDisplayField("Project Name", { name: "name" }),
    myForm.getTextareaField("Property", { name: "loanProperty_Streets" }),
  ]);

  myForm.addGroupFields([
    myForm.getDisplayField("City", { name: "city" }),
    myForm.getDisplayField("State", { name: "state" }),
    myForm.getDisplayField("Submarket", { name: "loanProject_Submarket" }),
    myForm.getDisplayField("Budget", { fieldType: "dollar", name: "loanBudgetAmount" }),
  ]);

  myForm.addCenteredGroup([
    myForm.getTextareaField("LRC Overview", { name:"loanUnderWriting_LrcOverview", width: 12 }),
  ]);

  myForm.addGroupFields([
    myForm.getDisplayField("Original Projected IRR", { name:"loanUnderWriting_ProjectedIRrOriginal" }),
    myForm.getDisplayField("Current LTV", { name: "loanUnderWriting_LtvPercentage" }),
    myForm.getDisplayField("Current CLTV", { name: "loanUnderWriting_CltVPercentage" }),
    myForm.getDisplayField("Current LTC", { name: "loanUnderWriting_LtvPercentage" }),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Direct Pay", { inline: true, width: 4 }),
    myForm.getDisplayField("How many homes have Guarantors completed and sold in last 18 months?", { width: 8 }),
    myForm.getDisplayField("Approved Liquidity Percentage", { fieldType:"percent", name: "loanUnderWriting_ApprovedLiquidityPercentage", width: 4 }),
  ]);

  const history = useHistory();
  const handleToViewLoanDetails = () => {
    history.push(`/loandetails/${myForm?.initialValues?.loanDetailId}`);
  };

  myForm.addCenteredGroup([
    myForm.getButtonField("View Loan Details", () => handleToViewLoanDetails()),
  ]);

  myForm.addComponent(
    <LmsTable
      apiSuffix={`voting/loan-review/list-loan-docs/${id}`}
      hideProps={["display"]}
      columnLabelsDictionary={{
        createdOn: "Upload Date", 
        documentCategoryName: "Document Category", 
        name: "Document Master",
        fileName: "Document Name",
      }}
      title="Documents"
      columnClickOptions={{
        fileName: { downloadPath: "document/download", idProp: "id" },
      }}
      isDisableClickable
    />
  );

  if (loanReviewMembersVote?.length > 0) {
    myForm.addComponent(
      <LmsTable
        title="Members Vote"
        apiSuffix={`voting/loan-review/list-polling-results/${id}`}
        data={loanReviewMembersVote}
        displayOnlyProps={["organizationContact", "pollingResult", "pollingDate"]}
        columnLabelsDictionary={{
          organizationContact: "Member", 
          pollingResult: "Vote",
          pollingDate: "Date"
        }}
        isDisabled
      />
    );
  }

  myForm.addCenteredGroup([
    myForm.getDropdownField("Polling Result", "voting/list-polling-results", { width: 6 }),
    myForm.getButtonField("Add Condition", () => setIsAddConditionModalOpen(true)),
  ]);

  myForm.addCenteredGroup([
    myForm.getTextareaField("Comments", { width: 12 }),
  ]);

  myForm.addCenteredGroup([
    myForm.getTextareaField("Description", { name:"lrcvotingDescription", width: 12, inlineEdit: true }),
  ]);

  return myForm;
};

const LmsVotingLoanReviewEditForm = ({ id }) => {
  const [{ data: loanReviewData, isLoading, error, fetchData }] = useFetchData(`voting/loan-review/${id}`);
  const [loanReviewMembersVote, setLoanReviewMembersVote] = useState(undefined as any);
  const [loanPollingInfo, setLoanPullingInfo] = useState(undefined as any);
  const [isAddConditionModalOpen, setIsAddConditionModalOpen] = useState(false);
  const myForm = editLoanReviewFormField(
    new BCFormController({ ...loanReviewData, ...loanPollingInfo }, { 
      values : { state: loanReviewData?.state?.display, city: loanReviewData?.city?.display },
      submitSuffix: "voting/loan-review", 
      submitMethod: "PUT",
      dataFormatterFunc: (data) => { return { ...data, loanId: id }; },
      clearSuffixesOnSubmit: [`voting/loan-review/${id}`]
    }),
    id,
    setIsAddConditionModalOpen,
    loanReviewMembersVote
  );

  const tabPages = {
    Detail: <LmsVotingDetailsTable suffix={`voting/loan-review/list-loan-details/${id}`} />,
    Properties: <LmsLoanPropertyTable suffix={`loans/properties/${id}`} loanId={id} />,
    Entities: <LmsLoanDetailsEntitiesTable  suffix={`loans/entities/${id}`} loanDetailsId={id} />,
    Comments: <LmsLoanCommentsTable suffix={`loandetails/comments/${id}`} loanId={id} loanDetailsId={id} />,
    Condition: <LmsTable actionButtons={["export"]} apiSuffix={`loans/conditions/${id}`} /> 
  };

  const formActions = ["save", "save-close", "close"];

  useEffect(() => {
    if (loanReviewData) {
      if (loanReviewData?.id) {
        const getLoanReviewMembersVote = async () => {
          const { data: bankList } = await fetchData(`voting/loan-review/list-polling-results/${loanReviewData?.id}`, { fetchMethod: "GET" });
          setLoanReviewMembersVote(bankList);
        };

        const getLoanPollingInfo = async () => {
          const { data: loanPollingData } = await fetchData(`voting/loan-polling-info/${id}`, { fetchMethod: "GET" });
          setLoanPullingInfo(loanPollingData);
        };
        
        getLoanReviewMembersVote();   
        getLoanPollingInfo();
      }
    }
  }, [loanReviewData]);

  return (<>
    <BCModal
      isModalOpen={isAddConditionModalOpen}
      size="fullscreen"
      closeIcon
      onClose={() => setIsAddConditionModalOpen(false)}
      content={<AddLoanConditionsForm conditionSource="Loan Review" loanId={id} />}
    />
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />
    {tabPages && <LmsTabPages tabPages={tabPages}/>}
  </>);
};

const LoanReviewMainPage = () => {
  return <LmsTable 
    actionButtons={["edit", "export", "save", "cancel"]} 
    apiSuffix="voting/list-loan-pollings"
    columnLabelsDictionary={{
      loanNumber: "Loan #",
      borrowerOrganization: "Borrower",
      lrcvotingDescription: "Description",
      perVoted: "Voting %",
      applicationNumber: "Application #",
    }}
    myForm={editLoanReviewFormField(new BCFormController())}
    saveSuffix="voting/lrc-voting-description"
  />;
};

const LmsVotingLoanReview = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params, ["edit"]);

  if (!isActionAllowed) {
    history.goBack();
  }

  return <React.Fragment>
    {isMainPage && <LoanReviewMainPage />}
    {isEdit && <LmsVotingLoanReviewEditForm id={id} />}
  </React.Fragment>;
};

export default LmsVotingLoanReview;